import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'superadmin';
  allowedRoles?: ('admin' | 'superadmin')[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  allowedRoles
}) => {
  const { isAuthenticated, userRole, loading } = useAuth();
  const location = useLocation();

  // Treat "authenticated but role not yet resolved" as loading to avoid flicker
  if (loading || (isAuthenticated && !userRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-500 mx-auto mb-4"></div>
          <p className="text-navy-200">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role permissions
  const hasRequiredRole = requiredRole
    ? (requiredRole === 'admin'
      ? userRole === 'admin' || userRole === 'superadmin'
      : userRole === requiredRole)
    : true;
  const hasAllowedRole = allowedRoles ? allowedRoles.includes(userRole!) : true;
  
  if (!hasRequiredRole || !hasAllowedRole) {
    // Redirect to appropriate dashboard instead of showing error
    const redirectPath = '/admin/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
